import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';
import TeamNavbar from '../components/TeamNavbar';

const TeamSwagStore = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTeamData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        
        if (userData?.teamId) {
          const teamDoc = await getDoc(doc(db, 'teams', userData.teamId));
          if (teamDoc.exists()) {
            setTeam(teamDoc.data());
          }
        }
      } catch (error) {
        console.error('Error loading team data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading swag store...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Team Required</h1>
          <p className="text-gray-400 mb-6">You need to be part of a team to access the swag store.</p>
          <button
            onClick={() => navigate('/team/profile')}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Create or Join Team
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Team Navigation Bar */}
      <TeamNavbar />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Coming Soon Banner */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-8 mb-8 text-center">
            <div className="text-6xl mb-4">🛍️</div>
            <h2 className="text-3xl font-bold text-white mb-4">Swag Store Coming Soon!</h2>
            <p className="text-purple-100 text-lg mb-6">
              We're working on bringing you custom team gear with your colors and logo!
            </p>
            <div className="bg-white/10 rounded-lg p-4 inline-block">
              <p className="text-white font-semibold">Expected Launch: Q1 2026</p>
            </div>
          </div>

          {/* Team Info */}
          <div className="bg-gray-800 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">{team.name} Team Gear</h3>
                <p className="text-gray-400">Your team colors and branding will be featured on all products</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-8 h-8 rounded" style={{ backgroundColor: team.colors?.primary || '#10B981' }}></div>
                <div className="w-8 h-8 rounded" style={{ backgroundColor: team.colors?.secondary || '#1F2937' }}></div>
                <span className="text-white text-sm">Team Colors</span>
              </div>
            </div>
          </div>

          {/* Product Preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* T-Shirts Preview */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="text-4xl mb-4 text-center">👕</div>
              <h3 className="text-lg font-bold text-white mb-2">Team T-Shirts</h3>
              <p className="text-gray-400 text-sm mb-4">Custom t-shirts with your team logo and colors</p>
              <div className="text-gray-500 text-sm">Coming Soon</div>
            </div>

            {/* Hoodies Preview */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="text-4xl mb-4 text-center">🧥</div>
              <h3 className="text-lg font-bold text-white mb-2">Team Hoodies</h3>
              <p className="text-gray-400 text-sm mb-4">Warm hoodies featuring your team branding</p>
              <div className="text-gray-500 text-sm">Coming Soon</div>
            </div>

            {/* Hats Preview */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="text-4xl mb-4 text-center">🧢</div>
              <h3 className="text-lg font-bold text-white mb-2">Team Hats</h3>
              <p className="text-gray-400 text-sm mb-4">Baseball caps and beanies with embroidered logos</p>
              <div className="text-gray-500 text-sm">Coming Soon</div>
            </div>

            {/* Accessories Preview */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="text-4xl mb-4 text-center">🎒</div>
              <h3 className="text-lg font-bold text-white mb-2">Team Accessories</h3>
              <p className="text-gray-400 text-sm mb-4">Tote bags, keychains, and other accessories</p>
              <div className="text-gray-500 text-sm">Coming Soon</div>
            </div>

            {/* Drinkware Preview */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="text-4xl mb-4 text-center">🥤</div>
              <h3 className="text-lg font-bold text-white mb-2">Team Drinkware</h3>
              <p className="text-gray-400 text-sm mb-4">Tumblers and water bottles with team logos</p>
              <div className="text-gray-500 text-sm">Coming Soon</div>
            </div>

            {/* Mystery Item */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="text-4xl mb-4 text-center">🎁</div>
              <h3 className="text-lg font-bold text-white mb-2">Surprise Items</h3>
              <p className="text-gray-400 text-sm mb-4">Special team gear and exclusive items</p>
              <div className="text-gray-500 text-sm">Coming Soon</div>
            </div>
          </div>

          {/* Features Preview */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">What to Expect</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <div className="text-green-500 text-xl">✓</div>
                <div>
                  <h4 className="text-white font-semibold">Custom Team Colors</h4>
                  <p className="text-gray-400 text-sm">All products will use your team's primary and secondary colors</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="text-green-500 text-xl">✓</div>
                <div>
                  <h4 className="text-white font-semibold">Team Logo Integration</h4>
                  <p className="text-gray-400 text-sm">Your team profile picture will be featured on all products</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="text-green-500 text-xl">✓</div>
                <div>
                  <h4 className="text-white font-semibold">Quality Materials</h4>
                  <p className="text-gray-400 text-sm">Premium fabrics and durable construction for long-lasting gear</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="text-green-500 text-xl">✓</div>
                <div>
                  <h4 className="text-white font-semibold">Team Orders</h4>
                  <p className="text-gray-400 text-sm">Bulk ordering for entire teams with volume discounts</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notification Signup */}
          <div className="bg-green-900/20 border border-green-600 rounded-xl p-6 mt-8">
            <h3 className="text-xl font-bold text-green-400 mb-4">Get Notified When We Launch!</h3>
            <p className="text-gray-300 mb-4">
              Be the first to know when the swag store opens and get early access to team gear.
            </p>
            <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded">
              Notify Me When Available
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamSwagStore; 